import { ChevronDown, ChevronUp, HelpCircle, MessageCircle, ThumbsUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import { websocketService } from '../../services/websocket'
import './ProjectQuestions.css'

import { API_URL } from '../../config/api'
interface Question {
  id: number
  question: string
  answer: string | null
  status: 'open' | 'answered'
  asked_by: number
  answered_by: number | null
  agent_id: number | null
  agent_name?: string
  user_name?: string
  upvotes: number
  created_at: string
  answered_at: string | null
}

interface ProjectQuestionsProps {
  projectId: number
  isResearch?: boolean
}

export default function ProjectQuestions({ projectId, isResearch }: ProjectQuestionsProps) {
  const [questions, setQuestions] = useState<Question[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'open' | 'answered'>('all')
  const [expandedId, setExpandedId] = useState<number | null>(null)

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const endpoint = isResearch
          ? `${API_URL}/api/research/projects/${projectId}/questions`
          : `${API_URL}/api/projects/${projectId}/questions`
        const res = await fetch(endpoint)
        const data = await res.json()
        setQuestions((data.questions || []) as Question[])
      } catch (err) {
        console.error('Questions load error:', err)
      } finally {
        setLoading(false)
      }
    }
    void loadQuestions()

    // WebSocket real-time updates - once connect, sonra subscribe
    websocketService.connect(0).then(() => {
      websocketService.subscribe('projects')
    }).catch(() => {
      // WebSocket optional - ignore
    })
    const unsubscribe = websocketService.on('project_update', (event) => {
      interface ProjectEventData {
        project_id: number;
        event_type: string;
        data: Question;
      }
      const data = event.data as ProjectEventData | undefined
      if (data && data.project_id === projectId && data.event_type === 'question_asked') {
        setQuestions(prev => [data.data, ...prev])
      }
    })

    return () => {
      unsubscribe()
    }
  }, [projectId])

  const getAgentGradient = (index: number) => {
    const gradients = [
      'linear-gradient(135deg, #667eea, #764ba2)',
      'linear-gradient(135deg, #10b981, #059669)',
      'linear-gradient(135deg, #ec4899, #db2777)'
    ]
    return gradients[index % gradients.length]
  }

  const [newQuestion, setNewQuestion] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleAskQuestion = async () => {
    if (!newQuestion.trim() || newQuestion.length < 10) return
    setSubmitting(true)
    try {
      const endpoint = isResearch
        ? `${API_URL}/api/research/projects/${projectId}/questions`
        : `${API_URL}/api/projects/${projectId}/questions`
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: newQuestion, user_id: 1 }),
      })
      if (res.ok) {
        const data = await res.json() as { id: number }
        setQuestions(prev => [{ id: data.id, question: newQuestion, answer: null, status: 'open', asked_by: 1, answered_by: null, agent_id: null, upvotes: 0, created_at: new Date().toISOString(), answered_at: null }, ...prev])
        setNewQuestion('')
      }
    } catch {} finally { setSubmitting(false) }
  }

  if (loading) {return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}

  return (
    <div className="questions-container">
      {/* Ask Question Form */}
      <div style={{ marginBottom: 16, padding: 16, background: 'var(--body-bg)', borderRadius: 10, border: '1px solid var(--border-color)' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Ask a Question</div>
        <textarea
          value={newQuestion}
          onChange={e => setNewQuestion(e.target.value)}
          placeholder="What would you like to know about this research?"
          style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: 13, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            onClick={() => { void handleAskQuestion() }}
            disabled={!newQuestion.trim() || newQuestion.length < 10 || submitting}
            style={{ padding: '8px 16px', borderRadius: 8, background: newQuestion.trim().length >= 10 ? '#667eea' : 'var(--body-bg)', border: 'none', color: newQuestion.trim().length >= 10 ? '#fff' : 'var(--text-secondary)', fontSize: 13, fontWeight: 600, cursor: newQuestion.trim().length >= 10 ? 'pointer' : 'not-allowed' }}
          >
            {submitting ? 'Submitting...' : 'Ask Question'}
          </button>
        </div>
      </div>

      <div className="questions-header">
        <div className="questions-filters">
          <button className={`filter-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All Questions</button>
          <button className={`filter-tab ${filter === 'open' ? 'active' : ''}`} onClick={() => setFilter('open')}>Open</button>
          <button className={`filter-tab ${filter === 'answered' ? 'active' : ''}`} onClick={() => setFilter('answered')}>Answered</button>
        </div>
      </div>

      {questions.length === 0 ? (
        <div className="questions-empty">
          <HelpCircle size={64} />
          <h3>No questions yet</h3>
          <p>Questions from agents and contributors will appear here</p>
        </div>
      ) : (
        <div className="questions-list">
          {questions.map((q, index) => {
            const agentName = q.agent_name || q.user_name || `Researcher ${q.asked_by || index + 1}`
            const agentInitial = agentName.charAt(0).toUpperCase()
            const isExpanded = expandedId === q.id

            return (
              <div key={q.id} className="question-card">
                <div className="question-header">
                  <div className="question-avatar" style={{ background: getAgentGradient(index) }}>{agentInitial}</div>
                  <div className="question-meta">
                    <div className="question-agent-name">{agentName}</div>
                    <div className="question-time">{new Date(q.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className={`question-status ${q.status}`}>{q.status === 'answered' ? 'Answered' : 'Open'}</div>
                </div>
                <div className="question-text">{q.question}</div>
                {q.answer && (
                  <div className={`answer-section ${isExpanded ? '' : 'collapsed'}`}>
                    <div className="answer-label">Answer</div>
                    <div className="answer-text">{q.answer}</div>
                    {q.answered_at && <div className="answer-by">Answered {new Date(q.answered_at).toLocaleDateString()}</div>}
                  </div>
                )}
                <div className="question-footer">
                  <div className="question-actions">
                    <button className="question-action-btn"><ThumbsUp size={14} />{q.upvotes || 0}</button>
                    <button className="question-action-btn"><MessageCircle size={14} />Comment</button>
                    {q.answer && (
                      <button className="question-action-btn" onClick={() => setExpandedId(isExpanded ? null : q.id)}>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {isExpanded ? 'Hide' : 'Show'} Answer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
